import DOMPurify from 'dompurify';

/** Must match WIDGET_HTML_CONTENT_PREFIX in backend/services/widget_ticket_service.py */
export const WIDGET_HTML_CONTENT_PREFIX = '<!--zento-content-format:html-->';

export function isWidgetHtmlMessage(content: string): boolean {
  return content.startsWith(WIDGET_HTML_CONTENT_PREFIX);
}

export function stripWidgetHtmlPrefix(content: string): string {
  let text = content;
  while (isWidgetHtmlMessage(text)) {
    text = text.slice(WIDGET_HTML_CONTENT_PREFIX.length).replace(/^\n/, '');
  }
  return text;
}

/** Plain-text snippet for conversation lists and notifications (matches backend preview helper). */
export function messageContentToPreview(
  value: string | null | undefined,
  maxLen = 60,
): string | null | undefined {
  if (!value) return value ?? null;

  let text = value;
  if (isWidgetHtmlMessage(text)) {
    text = stripWidgetHtmlPrefix(text);
  }

  let plain: string;
  if (typeof document !== 'undefined') {
    const sanitized = DOMPurify.sanitize(text, { USE_PROFILES: { html: true } });
    const tmp = document.createElement('div');
    tmp.innerHTML = sanitized;
    plain = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  } else {
    plain = text
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p\s*>/gi, '\n')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  if (!plain) return null;
  if (plain.length > maxLen) return plain.slice(0, maxLen) + '...';
  return plain;
}

/** Ensure mirrored ticket chips survive sanitization (anchors need href). */
function ensureTicketLinkHrefs(html: string): string {
  return html.replace(
    /<a\b([^>]*\bclass="[^"]*zento-message-ticket-link[^"]*"[^>]*)>/gi,
    (tag) => {
      if (/\bhref\s*=/.test(tag)) return tag;
      const idMatch = tag.match(/\bdata-ticket-id="([^"]+)"/i);
      if (!idMatch) return tag;
      return tag.replace('<a', `<a href="zento-ticket:${idMatch[1]}"`);
    },
  );
}

/** Replace in-widget ticket links with non-navigating chips for agent/inbox views. */
function neutralizeTicketLinks(html: string, chipClass: string): string {
  if (typeof document === 'undefined') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('a[href^="zento-ticket:"], a.zento-message-ticket-link').forEach((node) => {
    const span = doc.createElement('span');
    span.className = chipClass;
    span.textContent = node.textContent?.trim() || 'Ver ticket';
    node.replaceWith(span);
  });
  return doc.body.innerHTML;
}

const WIDGET_HTML_PURIFY_CONFIG = {
  USE_PROFILES: { html: true },
  ADD_ATTR: ['target', 'rel', 'data-ticket-id', 'role', 'tabindex', 'colspan', 'rowspan', 'class'],
};

/** Sanitize mirrored ticket HTML while preserving interactive ticket-link anchors. */
export function sanitizeWidgetHtmlForChat(html: string): string {
  const prepared = ensureTicketLinkHrefs(html);
  return DOMPurify.sanitize(prepared, WIDGET_HTML_PURIFY_CONFIG);
}

export function sanitizeWidgetHtmlMessage(html: string, ticketChipClass: string): string {
  return neutralizeTicketLinks(sanitizeWidgetHtmlForChat(html), ticketChipClass);
}
